<template>
  <div class="ttype">
    <v-snackbar v-model="alert.active" color="error">
      {{ alert.message }}
    </v-snackbar>

    <div class="ttype__head">
      <h2 class="ttype__title">{{ $t("property.create.type.instructions") }}</h2>
    </div>

    <div class="ttype__card">
      <v-form ref="form" v-model="valid" lazy-validation class="ttype__form">
        <!-- operation type -->
        <div class="ttype__block">
          <div class="ttype__label">
            {{ $t("property.create.type.fields.operation_type") }}
          </div>
          <v-btn-toggle v-model="property.post_type" mandatory multiple class="ttype__seg">
            <v-btn value="rent" :ripple="false">
              {{ $t("property.create.type.fields.rent") }}
            </v-btn>
            <v-btn value="sell" :ripple="false">
              {{ $t("property.create.type.fields.sell") }}
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- property type + owner -->
        <div class="ttype__grid">
          <v-autocomplete
            :rules="rules"
            :items="property_types"
            v-model="property.property_type"
            outlined
            dense
            :label="$t('property.create.type.fields.property_type')"
          ></v-autocomplete>

          <div class="ttype__owner">
            <v-autocomplete
              :rules="rules"
              :items="owners"
              item-text="profile.name"
              item-value="id"
              v-model="property.owner_ids"
              outlined
              dense
              multiple
              hide-details="auto"
              :label="
                $t(
                  'roles.' +
                    (property.post_type.includes('sell') ? 'sell' : 'rent') +
                    '.owners'
                )
              "
            ></v-autocomplete>
            <Modal
              ref="modal"
              text="+ Agregar a agenda"
              color="primary"
              :button_text="true"
            >
              <NewContact
                @close="$refs.modal.dialog = false"
                @created="onCreate"
              />
            </Modal>
          </div>
        </div>

        <!-- address (google) -->
        <div class="ttype__block" :key="owners.length">
          <vuetify-google-autocomplete
            outlined
            dense
            id="map"
            :label="$t('property.create.type.fields.address')"
            v-model="address"
            no-filter
            @placechanged="setPlace"
          ></vuetify-google-autocomplete>
        </div>

        <div class="ttype__or"><span>O cargá manualmente</span></div>

        <!-- manual address -->
        <div class="ttype__grid">
          <template
            v-if="manualAddress"
            v-for="field in [
              'street',
              'number',
              'city',
              'state',
              'country',
              'lat',
              'lng'
            ]"
          >
            <v-text-field
              v-if="['lat', 'lng'].includes(field)"
              :key="field"
              outlined
              dense
              :label="$t('property.create.type.fields.' + field)"
              v-model.number="property.address_attributes[field]"
            ></v-text-field>

            <v-text-field
              v-else
              :key="field"
              outlined
              dense
              :rules="rules"
              :label="$t('property.create.type.fields.' + field)"
              v-model="property.address_attributes[field]"
            ></v-text-field>
          </template>

          <v-text-field
            outlined
            dense
            :label="$t('property.create.type.fields.apartment_unit')"
            :hint="$t('property.create.type.fields.apartment_unit_hint')"
            persistent-hint
            v-model="property.address_attributes.unit"
          ></v-text-field>
        </div>

        <!-- map -->
        <div class="ttype__map">
          <GmapMap
            :center="{ lat: latitude, lng: longitude }"
            :zoom="16"
            :options="{
              zoomControl: true,
              mapTypeControl: false,
              scaleControl: false,
              streetViewControl: false,
              rotateControl: false,
              fullscreenControl: true,
              disableDefaultUi: true
            }"
            style="width: 100%; height: 200px"
          >
            <GmapMarker
              v-if="marker.lat && marker.lng"
              :position="marker"
              @click="center = marker"
            />
          </GmapMap>
        </div>
      </v-form>
    </div>

    <div class="ttype__footer">
      <v-btn text color="primary" class="rounded-lg" @click="$router.push({ name: 'Inmuebles' })">
        {{ fetchedProperty.id ? "Cerrar" : "Cancelar" }}
      </v-btn>
      <v-btn color="accent" depressed class="rounded-lg" @click="nextStep">
        {{ $t("action.next") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import Modal from "@/components/modals/Default";
import NewContact from "@/components/directories/New";
import { Contacts } from "@/graphql/queries/directory";
import { CreateProperty, EditProperty } from "@/graphql/mutations/properties";

export default {
  data() {
    return {
      alert: {
        active: false,
        message: null
      },
      manualAddress: true,
      valid: true,
      address: null,
      property: {
        id: null,
        price: 0,
        post_type: ["rent"],
        property_type: null,
        owner_ids: null,
        address_attributes: {
          id: null,
          street: null,
          number: null,
          unit: null,
          city: null,
          state: null,
          neighborhood: null,
          lat: null,
          lng: null,
          country: "Argentina"
        }
      },
      marker: {
        lat: null,
        lng: null
      },
      latitude: -38.4161,
      longitude: -63.6167,
      rules: [v => !!v || this.$t("required_fields_hint")],
      property_types: [
        {
          text: this.$t(
            "property.create.type.fields.property_types.country_house"
          ),
          value: "country_house"
        },
        {
          text: this.$t("property.create.type.fields.property_types.apartment"),
          value: "apartment"
        },
        {
          text: this.$t("property.create.type.fields.property_types.salon"),
          value: "salon"
        },
        {
          text: this.$t("property.create.type.fields.property_types.warehouse"),
          value: "warehouse"
        },
        {
          text: this.$t("property.create.type.fields.property_types.parking"),
          value: "parking"
        },
        {
          text: this.$t("property.create.type.fields.property_types.shed"),
          value: "shed"
        },
        {
          text: this.$t("property.create.type.fields.property_types.house"),
          value: "house"
        },
        {
          text: this.$t("property.create.type.fields.property_types.farm"),
          value: "farm"
        },
        {
          text: this.$t("property.create.type.fields.property_types.terrain"),
          value: "terrain"
        }
      ],
      owners: []
    };
  },

  props: {
    fetchedProperty: {
      type: Object,
      required: false,
      default: () => {
        return {
          id: null
        };
      }
    }
  },

  components: {
    Modal,
    NewContact
  },

  created() {
    this.fetchOwners(true);
  },

  watch: {
    "property.address_attributes.lat": {
      handler(val) {
        this.latitude = val;

        this.marker.lat = val;
      }
    },

    "property.address_attributes.lng": {
      handler(val) {
        this.longitude = val;
        this.marker.lng = val;
      }
    }
  },

  methods: {
    onCreate(newOwner) {
      this.fetchOwners(false);
      if (!this.property.owner_ids.length) this.property.owner_ids = [];

      this.property.owner_ids.push(newOwner.id);
      this.$refs.modal.dialog = false;
    },

    nextStep() {
      if (this.valid) this.valid = this.$refs.form.validate();

      if (this.valid) {
        if (this.property.id) {
          this.editProperty();
        } else {
          this.createProperty();
        }
      }
    },

    createProperty() {
      this.$apollo
        .mutate({
          mutation: CreateProperty,
          variables: {
            input: {
              attributes: {
                price: 0,
                propertyType: this.property.property_type,
                state: this.property.state || "draft",
                features: {
                  operation_type: {
                    sell: this.property.post_type.includes("sell"),
                    rent: this.property.post_type.includes("rent")
                  }
                },
                addressAttributes: this.property.address_attributes
              },
              ownerIds: this.property.owner_ids
            }
          }
        })
        .then(res => {
          var property = res.data.createProperty.property;
          this.property.id = property.id;
          this.$emit("continue", property);
        })
        .catch(error => {
          this.alert.active = true;
          this.alert.message = this.$t(
            `error.${error.graphQLErrors[0].extensions.field}.${error.graphQLErrors[0].extensions.message}`
          );
        });
    },

    editProperty() {
      this.$apollo
        .mutate({
          mutation: EditProperty,
          variables: {
            input: {
              attributes: {
                propertyType: this.property.property_type,
                state: this.property.state || "draft",
                features: Object.assign(this.fetchedProperty.features, {
                  operation_type: {
                    sell: this.property.post_type.includes("sell"),
                    rent: this.property.post_type.includes("rent")
                  }
                }),
                addressAttributes: this.property.address_attributes
              },
              ownerIds: this.property.owner_ids,
              propertyId: this.property.id
            }
          }
        })
        .then(res => {
          var property = res.data.editProperty.property;
          this.property.id = property.id;
          this.$emit("continue", property);
        });
    },

    fetchOwners(firstLoad = false) {
      this.$apollo
        .query({
          query: Contacts,
          variables: {
            noPaginate: true
          },
          fetchPolicy: "cache-first"
        })
        .then(res => {
          this.owners = res.data.contacts.contacts;

          if (this.$route.query.ownerId) {
            this.property.owner_ids = [this.$route.query.ownerId];
          }

          if (firstLoad) {
            this.fillData();
          }
        });
    },

    fillData() {
      this.property.id = this.fetchedProperty.id;
      this.property.price = this.fetchedProperty.price || 0;
      this.property.state = this.fetchedProperty.state;
      this.property.property_type =
        this.fetchedProperty.propertyType || this.property.property_type;

      if (this.fetchedProperty.owner)
        this.property.owner_ids =
          (this.fetchedProperty.owner || [{}]).map(owner => owner.id) ||
          this.property.owner_ids;

      this.property.address_attributes =
        this.fetchedProperty.address || this.property.address_attributes;

      delete this.property.address_attributes.__typename;

      if (this.fetchedProperty.features) {
        var features = this.fetchedProperty.features;
        var post_type = [];
        if (features.operation_type.rent) post_type.push("rent");
        if (features.operation_type.sell) post_type.push("sell");
        this.property.post_type = post_type;
      }
      if (this.fetchedProperty.address) {
        var address = this.fetchedProperty.address;
        this.address = `${address.street} ${address.number}, ${address.city}, ${address.state}, ${address.country}`;
      }
    },

    // vuetify-google-autocomplete emits (addressData, rawPlaceResult, id). The raw
    // result carries address_components with the barrio, which addressData omits.
    setPlace(place, raw) {
      this.property.address_attributes.lat = place.latitude;
      this.property.address_attributes.lng = place.longitude;
      this.property.address_attributes.street = place.route;
      this.property.address_attributes.number = place.street_number;
      this.property.address_attributes.city = place.locality;
      this.property.address_attributes.state =
        place.administrative_area_level_1;
      this.property.address_attributes.country = place.country;
      this.property.address_attributes.neighborhood = this.neighborhoodFrom(raw);

      this.latitude = place.latitude;
      this.longitude = place.longitude;

      this.marker.lat = place.latitude;
      this.marker.lng = place.longitude;
    },

    // pull the barrio from Google's components (most specific first); the backend
    // reverse-geocodes as a fallback when Google returns none here.
    neighborhoodFrom(raw) {
      const components = (raw && raw.address_components) || [];
      const types = ["neighborhood", "sublocality_level_1", "sublocality"];
      for (const type of types) {
        const match = components.find(c => (c.types || []).includes(type));
        if (match) return match.long_name;
      }
      return null;
    }
  }
};
</script>

<style scoped>
.ttype__head { margin-bottom: 16px; }
.ttype__eyebrow {
  font-size: 10.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
}
.ttype__title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1814;
  margin: 5px 0 0;
  line-height: 1.2;
}

.ttype__card {
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 14px;
  padding: 22px 22px 6px;
}

.ttype__label {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
  margin-bottom: 9px;
}

/* segmented operation toggle */
.ttype__seg {
  display: flex;
  width: 100%;
  border: 1px solid #e3ddd2;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 18px;
}
.ttype__seg >>> .v-btn {
  flex: 1;
  height: 44px !important;
  min-width: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  background: #fff !important;
  color: #56504a !important;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  font-size: 13.5px;
  opacity: 1 !important;
}
.ttype__seg >>> .v-btn:not(:last-child) { border-right: 1px solid #e3ddd2 !important; }
.ttype__seg >>> .v-btn.v-btn--active { background: #1a1814 !important; color: #fff !important; }
.ttype__seg >>> .v-btn__content { opacity: 1 !important; }

/* two-column field grid */
.ttype__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 16px;
}
/* "Agregar a agenda" as a tight left-aligned link under the owner field */
.ttype__owner >>> .v-btn {
  height: auto !important;
  min-width: 0 !important;
  padding: 0 !important;
  margin: 6px 0 16px;
  text-transform: none !important;
  letter-spacing: normal !important;
  font-size: 13px !important;
  font-weight: 600;
}
.ttype__owner >>> .v-btn__content { justify-content: flex-start; }
.ttype__owner >>> .v-btn::before { opacity: 0 !important; }

/* "o cargá manualmente" divider */
.ttype__or {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #a39a8c;
  font-size: 12px;
  margin: 4px 0 16px;
}
.ttype__or::before,
.ttype__or::after { content: ''; flex: 1; height: 1px; background: #ece7de; }

/* map */
.ttype__map {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #ece7de;
  margin-bottom: 16px;
}

/* footer */
.ttype__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

/* warm outlined fieldsets */
.ttype__form >>> .v-text-field--outlined fieldset { border-radius: 10px; }
.ttype__form >>> .v-text-field--outlined.v-input--is-focused fieldset { border-color: #b52118; }

@media (max-width: 600px) {
  .ttype__grid { grid-template-columns: 1fr; }
}
</style>
